<template>
  <preview-frame
    class="page"
    :class="previewPage.scopeClass"
    :initial-preview-type="PREVIEW_OPTIONS.DESKTOP.value"
    @update:model-value="handlePreviewTypeChange"
  >
    <template
      #sections
    >
      <div
        v-for="section in previewSections"
        :key="section.id"
      >
        <section-component
          :key="changeCount"
          :is-phone-preview="!isDesktop"
          :section="section"
          :demo-mode="true"
          :class="[section.class, !isDesktop ? undefined : 'zoom']"
        />
      </div>
    </template>
  </preview-frame>
</template>

<script>
import Page from 'src/models/page/Page'
import { PREVIEW_OPTIONS } from 'src/models/page/Section'
import PreviewFrame from 'src/modules/consumer/components/PreviewFrame'
import SectionComponent from 'src/modules/consumer/pages/SectionComponent'
/**
 * We unfortunately need to `provide` this down to descendant components because PreviewPage
 * falls under the unique case of being used in an app mode that never changes $i18n.locale,
 * e.g. Admin, but we still want to make sure descendant previewed components instantiate their
 * models using the previewed Page model's language instead of the app's language. We pass down an
 * object to maintain a reference for changes because "provided" values do not trigger reactivity.
 */
const previewContext = {
  languageOverrideForNewModels: null
}

export default {
  name: 'PreviewPage',
  components: {
    SectionComponent,
    PreviewFrame
  },
  provide () {
    return {
      previewContext
    }
  },
  props: {
    page: {
      type: Page,
      required: true
    }
  },
  data () {
    return {
      previewPage: null,
      changeCount: 0,
      previewType: PREVIEW_OPTIONS.DESKTOP.value
    }
  },
  computed: {
    previewSections () {
      if (!this.previewPage) {
        return []
      }
      if (this.previewType === PREVIEW_OPTIONS.DESKTOP.value) {
        return _.filter(this.previewPage.sections.data, section => section.visible && section.desktop)
      } else if (this.previewType === PREVIEW_OPTIONS.MOBILE.value) {
        return _.filter(this.previewPage.sections.data, section => section.visible && section.mobile)
      } else {
        return _.filter(this.previewPage.sections.data, section => section.visible && section.app)
      }
    },
    isDesktop () {
      return this.previewType === PREVIEW_OPTIONS.DESKTOP.value
    }
  },
  watch: {
    'page.loading' () {
      if (this.page.loaded && !this.page.loading) {
        // finished reloading after a save/fetch
        this.throttledUpdatePreviewPage()
      }
    },
    'page.transformedStyles': {
      handler () {
        this.$mh.setCustomPageStyles(this.page.transformedStyles)
        this.throttledUpdatePreviewPage()
      },
      immediate: true
    },
    'previewPage.language': {
      handler () {
        if (this.previewPage) {
          previewContext.languageOverrideForNewModels = this.previewPage.language
        }
      }
    }
  },
  created () {
    // This needs to be set before descendant components' created() hooks run
    if (this.page) {
      previewContext.languageOverrideForNewModels = this.page.language
      this.throttledUpdatePreviewPage()
    }
    _.extend(this, { PREVIEW_OPTIONS })
  },
  unmounted () {
    this.$mh.setCustomPageStyles(null)
  },
  methods: {
    // We clone the model on each update, which can be expensive, so we debounce it
    throttledUpdatePreviewPage () {
      if (!this._throttledUpdatePreviewPage) {
        this._throttledUpdatePreviewPage = _.throttle(this.updatePreviewPage, 300)
      }
      this._throttledUpdatePreviewPage()
    },
    // To avoid bad reactivity side effects, we prevent LocalizedJsonObjects from proxying with translation
    // keys in Admin mode (where translations editing is allowed). But we still want to preview the content
    // in Admin, so our workaround is to keep a clone of the original section updated, with the cloned
    // model.previewing = true allowing LocalizedJsonObjects to proxy with translation keys. We intentionally
    // do not use model.clone() because we need to set the `previewing` reactive property before the data is
    // set and made reactive, so that any LocalizedJsonObjects are initialized and proxied before reactivity.
    updatePreviewPage () {
      this.previewPage = new this.page.constructor({ options: this.page.options })
      this.previewPage.language = this.page.language
      this.previewPage.previewing = true
      this.previewPage.setData(this.page.deepCloneData())
      _.each(this.previewPage.sections.data, section => {
        section.previewing = true
        section.language = this.page.language
      })
    },
    handlePreviewTypeChange (type) {
      this.changeCount++
      this.previewType = type
    }
  }
}
</script>
